/**
 * Bero design tokens — single source of truth, ported from design/source/styles.css.
 * LOCKED palette. Do not add colors; do not hardcode hex in components.
 */

export type ThemeColors = {
  canvas0: string; canvas1: string; canvas2: string;
  borderSoft: string; border: string; borderFirm: string;
  textPrimary: string; textSecondary: string; textTertiary: string; textOnDark: string;
  primary50: string; primary100: string; primary400: string; primary500: string;
  primary600: string; primary700: string; primary900: string;
  accent100: string; accent500: string; accent700: string;
  danger50: string; danger500: string; danger700: string;
  warning50: string; warning500: string; warning700: string;
  success50: string; success500: string; success700: string;
  info500: string; infoTint: string; scrim: string;
  /** redesign v3: tinted greeting band — the ONLY sanctioned gradient */
  headerBandFrom: string; headerBandTo: string;
};

export const palette: Record<'light' | 'dark', ThemeColors> = {
  light: {
    canvas0: '#FDFCF9',
    canvas1: '#F7F5F0',
    canvas2: '#EFECE4',
    borderSoft: '#E5E1D6',
    border: '#D4CEC0',
    borderFirm: '#B8B09E',
    textPrimary: '#1A1A17',
    textSecondary: '#4A4843',
    textTertiary: '#767163',  // a11y 2026-07-20: was #8B8679 (3.5:1) — now 4.75:1 on canvas0 (WCAG AA)
    textOnDark: '#FDFCF9',
    primary50: '#F1F5F1',
    primary100: '#DCE8DC',
    primary400: '#5E8A60',
    primary500: '#3B6B3E',
    primary600: '#2F5632',
    primary700: '#254327',
    primary900: '#15281A',
    accent100: '#FBF1D9',
    accent500: '#B8892A',
    accent700: '#8B6419',
    danger50: '#FBEEEC',
    danger500: '#B84738',
    danger700: '#8B3328',
    warning50: '#FDF4DF',
    warning500: '#B8832A',
    warning700: '#8B6219',
    success50: '#EDF4ED',
    success500: '#4A7D4E',
    success700: '#355B38',
    info500: '#4A5568',
    infoTint: 'rgba(74,85,104,0.10)',
    scrim: 'rgba(26,26,23,0.4)',
    headerBandFrom: '#F7F5F0',
    headerBandTo: '#F1F5F1',
  },
  dark: {
    canvas0: '#1A1A17',
    canvas1: '#232320',
    canvas2: '#2D2D29',
    borderSoft: '#3A3933',
    border: '#4A4843',
    borderFirm: '#5A584E',
    textPrimary: '#F0EEE7',
    textSecondary: '#BCB9AF',
    textTertiary: '#8F8B82',  // a11y 2026-07-20: was #7C7870 (3.97:1) — now 5.1:1 on canvas0 (WCAG AA)
    textOnDark: '#F0EEE7',
    primary50: 'rgba(111,161,114,0.12)',
    primary100: 'rgba(111,161,114,0.20)',
    primary400: '#5E8A60',
    primary500: '#6FA172',
    primary600: '#7FAE82',
    primary700: '#8FBB92',
    primary900: '#CFE3D0',
    accent100: 'rgba(212,165,61,0.16)',
    accent500: '#D4A53D',
    accent700: '#E0BC68',
    danger50: 'rgba(210,117,101,0.16)',
    danger500: '#D27565',
    danger700: '#E29A8D',
    warning50: 'rgba(212,165,61,0.14)',
    warning500: '#D4A53D',
    warning700: '#E0BC68',
    success50: 'rgba(111,161,114,0.14)',
    success500: '#7FAE82',
    success700: '#9CC49E',
    info500: '#8B93A3',
    infoTint: 'rgba(139,147,163,0.16)',
    scrim: 'rgba(0,0,0,0.55)',
    headerBandFrom: '#232320',
    headerBandTo: '#242B24',
  },
};

export type ColorScheme = keyof typeof palette;

/** Type ramp — sizes/lineHeights/weights from the locked spec. */
export const type = {
  displayXl: { fontSize: 32, lineHeight: 40, letterSpacing: -0.64, fontWeight: '700' as const },
  displayLg: { fontSize: 26, lineHeight: 32, letterSpacing: -0.52, fontWeight: '700' as const },
  headingLg: { fontSize: 22, lineHeight: 28, letterSpacing: -0.22, fontWeight: '600' as const },
  headingMd: { fontSize: 18, lineHeight: 24, letterSpacing: -0.18, fontWeight: '600' as const },
  headingSm: { fontSize: 16, lineHeight: 22, letterSpacing: 0, fontWeight: '600' as const },
  bodyLg: { fontSize: 16, lineHeight: 24, letterSpacing: 0, fontWeight: '400' as const },
  bodyMd: { fontSize: 14, lineHeight: 20, letterSpacing: 0, fontWeight: '400' as const },
  bodySm: { fontSize: 13, lineHeight: 18, letterSpacing: 0, fontWeight: '400' as const },
  label: { fontSize: 11, lineHeight: 16, letterSpacing: 0.66, fontWeight: '500' as const, textTransform: 'uppercase' as const },
  monoMd: { fontSize: 14, lineHeight: 20, letterSpacing: 0, fontWeight: '400' as const },
} as const;

export const space = { s1: 4, s2: 8, s3: 12, s4: 16, s5: 20, s6: 24, s8: 32, s10: 40, s12: 48, s16: 64 } as const;

export const radius = { sm: 4, md: 8, lg: 12, xl: 16, xxl: 24, full: 9999 } as const;

/** DEPRECATED (quiet-luxury pass 2026-07-19): drop shadows are banned
 * app-wide — user rule. Use `glow()` for the few floating/hero moments;
 * borders + fills everywhere else. Kept only so stale imports fail soft. */
export const elevation = {
  e1: { shadowColor: '#1A1A17', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 2, elevation: 1 },
  e2: { shadowColor: '#1A1A17', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 2 },
  e3: { shadowColor: '#1A1A17', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.08, shadowRadius: 12, elevation: 4 },
  e4: { shadowColor: '#1A1A17', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.12, shadowRadius: 24, elevation: 8 },
} as const;

/** Peripheral glow — a soft, CENTERED halo in a brand color (never a grey
 * offset drop shadow). Reserved for floating elements (FABs) and the one or
 * two hero moments a screen earns. Subtle by default; resist turning it up.
 * (Android's elevation can't tint, so native Android simply renders no halo.) */
export function glow(color: string, opacity = 0.28, r = 16) {
  return { shadowColor: color, shadowOffset: { width: 0, height: 0 }, shadowOpacity: opacity, shadowRadius: r, elevation: 0 } as const;
}

/** Fonts — load Inter + JetBrains Mono via expo-font; these are family keys. */
export const fonts = {
  regular: 'Inter_400Regular',
  medium: 'Inter_500Medium',
  semibold: 'Inter_600SemiBold',
  bold: 'Inter_700Bold',
  mono: 'JetBrainsMono_400Regular',
  monoMedium: 'JetBrainsMono_500Medium',
  /** Display voice (quiet-luxury pass 2026-07-19): Sora, reserved for the
   * few hero moments per screen — greetings, big numbers. Never body text. */
  display: 'Sora_600SemiBold',
} as const;

/** Status pill palette per spec (light-mode tints come from theme colors). */
export const pillStatus = ['open', 'progress', 'urgent', 'resolved', 'approved', 'pending', 'rejected'] as const;
export type PillStatus = (typeof pillStatus)[number];

/** ₹ formatting with Indian digit grouping: 124000 → "₹1,24,000". */
export function formatINR(amount: number, opts: { decimals?: number } = {}): string {
  const neg = amount < 0;
  const [int, frac] = Math.abs(amount).toFixed(opts.decimals ?? 0).split('.');
  const last3 = int.slice(-3);
  const rest = int.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  const grouped = rest ? `${rest},${last3}` : last3;
  return `${neg ? '-' : ''}₹${grouped}${frac ? '.' + frac : ''}`;
}
